import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { num, P } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { useGo } from '../../../navigation/nav';
import { Avatar, BarChart, Button, Card, Columns, EmptyState, ErrorState, Grid, ListItem, Loading, PageHeader, Screen, StatCard, StatusBadge } from '../../../ui';

/** Headline HR numbers from /dashboard/hr. */
export function HrDashboardScreen() {
  const go = useGo();
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['dashboard', 'hr'], queryFn: () => api.dashboard.hr() });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data)
    return (
      <Screen>
        <PageHeader title="HR dashboard" />
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </Screen>
    );
  const d = q.data;
  const manage = can(P.EMPLOYEES_MANAGE);
  const dept = [...d.by_department].sort((a, b) => b.count - a.count).slice(0, 12);
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="hr-dashboard">
      <PageHeader
        title="HR dashboard"
        actions={
          manage ? (
            <>
              <Button icon="upload" title="Bulk import" onPress={() => go('BulkImport')} />
              <Button variant="primary" icon="plus" title="Add employee" onPress={() => go('EmployeeNew')} />
            </>
          ) : undefined
        }
      />
      <Grid min={165}>
        <StatCard label="Headcount" value={num(d.headcount, 0)} icon="users" onPress={() => go('EmployeeList')} />
        <StatCard label="Joiners this month" value={d.joiners_this_month} icon="plus" tone="success" />
        <StatCard label="Exits this month" value={d.exits_this_month} icon="logout" tone="danger" />
        <StatCard label="Attrition (annualised)" value={`${num(d.attrition_rate_pct)}%`} icon="chart" tone="warning" onPress={can(P.REPORTS_FULL) ? () => go('Reports', { key: 'attrition' }) : undefined} />
        <StatCard label="Pending onboarding" value={d.pending_onboarding} icon="briefcase" tone="info" onPress={() => go('EmployeeList', { status: 'onboarding' })} />
        <StatCard label="Open tickets" value={d.open_tickets} icon="help" tone="neutral" onPress={can(P.HELPDESK_MANAGE) ? () => go('HelpdeskQueue') : undefined} />
        <StatCard label="Pending approvals" value={d.pending_approvals} icon="inbox" tone="warning" onPress={() => go('Approvals')} />
      </Grid>
      <Columns>
        <Card title="Headcount by department">
          {dept.length ? <BarChart data={dept.map((x) => ({ label: x.department || 'Unassigned', value: x.count }))} /> : <EmptyState icon="chart" title="No data yet" />}
        </Card>
        <Card title="Upcoming confirmations" subtitle="Probation ending soon" flush>
          {d.upcoming_confirmations.length ? (
            d.upcoming_confirmations.map((e, i) => (
              <ListItem key={e.id} left={<Avatar name={e.full_name} />} title={e.full_name} subtitle={`${e.emp_code} · ${e.designation ?? ''}`} right={<StatusBadge status={e.status} />} chevron onPress={() => go('EmployeeDetail', { id: e.id })} divider={i < d.upcoming_confirmations.length - 1} />
            ))
          ) : (
            <EmptyState icon="checkCircle" title="None due" />
          )}
        </Card>
      </Columns>
    </Screen>
  );
}
